/**
 * The decisions behind the locked kiosk screen's "an order is waiting" poll,
 * as plain functions.
 *
 * They live outside the React hook because they are the only part with real
 * consequences — whether the shop is told about an order twice, once, or never
 * — and a hook cannot be exercised under this repo's node test environment.
 *
 * STRUCTURAL TYPES, NOT THE API'S. `platform/` is the seam layer: `nfc`,
 * `printer`, `shell`, `alertChime` all depend on nothing above them, which is
 * what lets them be reasoned about alone. Importing the response shape from
 * `api/` inverted that, so the little of it these functions actually read is
 * declared here instead.
 */

/** The fields these decisions read. Any richer order shape satisfies it. */
export interface WaitingOrderLike {
  id: string;
  kind: string;
  amountEur: string | null;
  since: string;
}

export interface WaitingSnapshotLike {
  orders: ReadonlyArray<WaitingOrderLike>;
  totalEur: string;
  totalIsPartial: boolean;
}

/** Consecutive failed polls before the screen stops claiming to know. */
export const UNREACHABLE_AFTER_FAILURES = 2;

/**
 * How much the screen may claim to know.
 *
 * THREE STATES, NOT TWO. A boolean conflated "I asked and the answer was
 * nothing" with "I have not managed to ask yet", so a tablet armed during an
 * outage showed a calm, unmarked screen positively asserting that no work was
 * waiting — the very failure the reachability work was meant to remove, merely
 * shortened to the first thirty seconds.
 */
export type Freshness = "unknown" | "fresh" | "stale";

export function freshnessFrom(
  everSucceeded: boolean,
  consecutiveFailures: number,
): Freshness {
  // Repeated failures outrank a stale success: either way the answer on screen
  // is not to be trusted.
  if (consecutiveFailures >= UNREACHABLE_AFTER_FAILURES) return "stale";
  // One dropped poll is tolerated, because a single blip every fifteen seconds
  // is normal on shop wifi and flickering a warning trains people to ignore it.
  return everSucceeded ? "fresh" : "unknown";
}

/**
 * Ids present now that have not been announced yet.
 *
 * Answers "what is NEW", not "is there anything" — the distinction the chime
 * depends on, because a second order landing behind an unclaimed first one has
 * to make its own sound.
 */
export function freshOrderIds(
  announced: ReadonlySet<string>,
  orders: ReadonlyArray<{ id: string }>,
): string[] {
  return orders.map((order) => order.id).filter((id) => !announced.has(id));
}

/**
 * Whether two answers say the same thing.
 *
 * The overwhelmingly common poll on a kiosk is "still nothing", and storing a
 * freshly-parsed object for it re-renders the whole locked screen four times a
 * minute for a twelve-hour session.
 *
 * KEYED BY ID, NOT BY POSITION. The server sorts by `since`, whose comparator
 * returns 0 for ties, and Postgres does not promise a stable order for those —
 * so two orders created in the same millisecond can arrive [a,b] then [b,a]
 * with nothing changed. A positional compare called that a change and
 * re-rendered anyway, defeating the only thing this function is for.
 */
export function sameWaitingOrders(
  a: WaitingSnapshotLike,
  b: WaitingSnapshotLike,
): boolean {
  if (
    a.totalEur !== b.totalEur ||
    a.totalIsPartial !== b.totalIsPartial ||
    a.orders.length !== b.orders.length
  ) {
    return false;
  }
  const byId = new Map(b.orders.map((order) => [order.id, order]));
  return a.orders.every((order) => {
    const other = byId.get(order.id);
    return (
      other !== undefined &&
      other.amountEur === order.amountEur &&
      other.kind === order.kind &&
      other.since === order.since
    );
  });
}
